import { zodResolver } from "@hookform/resolvers/zod";
import MuxUploader from "@mux/mux-uploader-react";
import { useCallback, useId, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMutation } from "urql";
import { z } from "zod";

import { VideoPreview } from "@/components/VideoPreview";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { UpdateVideoMetadataInput } from "@/gen/graphql";
import { UpdateVideoForm_Video } from "@/routes/video/queries";

const zUpdateVideoInput = z.object({
    videoId: z.string(),
    thumbnailImageId: z.string().nullable(),
    title: z.string().optional(),
    youtubeId: z.preprocess(
        input => {
            if (typeof input === "string") {
                const trimmed = input.trim();
                return trimmed.length === 0 ? null : trimmed;
            }
        },
        z
            .string()
            .regex(
                /^[A-Za-z0-9_-]{11}$/,
                "YouTube id is 11 characters long and consist of letters, numbers, '_' and '-'"
            )
            .optional()
            .nullable()
    ),
});

export const VideoForm = ({
    videoFragment,
    onSubmitSuccess = () => {},
}: {
    videoFragment?: FragmentType<typeof UpdateVideoForm_Video>;
    onSubmitSuccess?: () => void;
}) => {
    const video = getFragmentData(UpdateVideoForm_Video, videoFragment);

    const [videoUploadProgress, setVideoUploadProgress] = useState(video?.muxPlaybackId ? 100 : 0);

    const defaultValues = zUpdateVideoInput.parse({
        videoId: video?.id ?? "",
        thumbnailImageId: video?.thumbnailImageId ?? null,
        title: video?.title ?? undefined,
        youtubeId: video?.youtubeId,
    });

    const form = useForm<z.infer<typeof zUpdateVideoInput>>({
        mode: "onChange",
        resolver: zodResolver(zUpdateVideoInput),
        defaultValues,
    });

    const [_, createMuxUploadUrl] = useMutation(
        graphql(`
            mutation VideoInputCreateMuxUploadUrl {
                createMuxUploadUrl {
                    videoId
                    uploadUrl
                }
            }
        `)
    );

    const [{ error: _errorUpdateVideoMetadata }, updateVideoMetadata] = useMutation(
        graphql(`
            mutation CreateVideo_UpdateVideoMetadata($input: UpdateVideoMetadataInput!) {
                updateVideoMetadata(input: $input) {
                    id
                    thumbnailImageId
                    youtubeId
                    title
                }
            }
        `)
    );

    const getUploadUrl = useCallback(async () => {
        const res = await createMuxUploadUrl({});
        if (res.error || !res.data) {
            toast.error(
                `Failed to create upload URL: ${res.error?.graphQLErrors.toString() || "Unknown error"}`
            );
            return { uploadUrl: "", videoId: null };
        }

        const { uploadUrl, videoId } = res.data.createMuxUploadUrl;
        return { uploadUrl, videoId };
    }, [createMuxUploadUrl]);

    const onSubmit: SubmitHandler<UpdateVideoMetadataInput> = async input => {
        if (videoUploadProgress !== 100) {
            toast.warning("Please wait for the video to finish uploading.");
            return;
        }

        const result = await updateVideoMetadata({
            input,
        });

        if (result.error) {
            toast.error("Unable to submit video", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Video was submitted");

            onSubmitSuccess();
        }
    };

    const id = useId();

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {video?.muxPlaybackId ? (
                    <VideoPreview
                        video={{
                            id: video.id,
                            muxPlaybackId: video.muxPlaybackId,
                        }}
                    />
                ) : (
                    <FormField
                        control={form.control}
                        name="videoId"
                        render={() => (
                            <FormItem>
                                <FormLabel>Upload video</FormLabel>
                                <FormControl>
                                    <MuxUploader
                                        id={id}
                                        noDrop
                                        endpoint={async () => {
                                            const { uploadUrl, videoId } = await getUploadUrl();
                                            if (!uploadUrl || !videoId) return "";

                                            form.setValue("videoId", videoId, {
                                                shouldValidate: true,
                                            });

                                            return uploadUrl;
                                        }}
                                        onProgress={p => {
                                            console.log("progress", p);
                                            setVideoUploadProgress((p as CustomEvent).detail);
                                        }}
                                        onSuccess={() => {
                                            toast.success(
                                                "Video uploaded successfully. Remember to save the form!"
                                            );
                                            setVideoUploadProgress(100);
                                        }}
                                        onError={e => {
                                            toast.error("Failed to upload video: " + e);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                )}
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input placeholder="Title" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="youtubeId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>YouTube id</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="YouTube id"
                                    {...field}
                                    value={field.value ?? undefined}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="thumbnailImageId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Thumbnail</FormLabel>
                            <FormControl>
                                <ImageUploadInput
                                    imageId={field.value ?? null}
                                    onImageId={imageId => {
                                        form.setValue(field.name, imageId);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button
                    type="submit"
                    disabled={!form.formState.isValid || form.formState.isSubmitting}
                >
                    Save
                </Button>
            </form>
        </Form>
    );
};
